import { useState, useRef, useEffect, useCallback } from 'react';
import cn from '../../utils/cn';
import KoobSidebarBadge from './KoobSidebarBadge';
import KoobSidebarFlyout from './KoobSidebarFlyout';
import KoobTooltip from '../KoobTooltip';
import { useRouterContext } from './routerContext';

const baseClasses =
  'koob-sidebar-link relative flex items-center rounded-lg font-medium transition-colors duration-150 border-none w-full text-left cursor-pointer';

const inactiveClasses =
  'text-foreground/80 hover:bg-accent hover:text-foreground';

function isPathActive(pathname, to) {
  if (!to) return false;
  // Normalize: ensure both start with /
  const normalizedTo = to.startsWith('/') ? to : `/${to}`;
  return pathname.startsWith(normalizedTo);
}

function NavLinkItem({ link, collapsed, nested }) {
  const { NavLink, useLocation } = useRouterContext();
  const location = useLocation();
  const [showFlyout, setShowFlyout] = useState(false);
  const flyoutRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hasChildren = link.children && link.children.length > 0;
  const isParentActive =
    hasChildren && isPathActive(location.pathname, link.to);
  // `link.alwaysExpanded` pins the group open: children always visible, no
  // chevron, and the parent behaves as a plain link (Trip Designer). Other
  // parents start expanded only when their route is active and toggle via
  // the chevron.
  const alwaysExpanded = hasChildren && Boolean(link.alwaysExpanded);
  const [expanded, setExpanded] = useState(isParentActive);
  const isExpanded = alwaysExpanded || expanded;

  // Extra path prefixes (beyond `link.to`) that should also highlight this
  // link as active, e.g. a compose/editor screen reached from a "create"
  // entry point. Optional and generic so any app's link config can use it
  // without hardcoding routes in this shared component.
  const isExtraMatchActive =
    Array.isArray(link.activeMatch) &&
    link.activeMatch.some((path) => isPathActive(location.pathname, path));

  useEffect(() => {
    if (isParentActive && !expanded) {
      setExpanded(true);
    }
  }, [isParentActive]); // eslint-disable-line react-hooks/exhaustive-deps

  const openFlyout = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setShowFlyout(true);
  }, []);

  const closeFlyoutWithDelay = useCallback(() => {
    closeTimeoutRef.current = setTimeout(() => {
      setShowFlyout(false);
      closeTimeoutRef.current = null;
    }, 150);
  }, []);

  // For parent links with children, prevent navigation and toggle expand.
  // Pinned groups skip the toggle: their parent navigates like a plain link.
  const handleParentClick = (e) => {
    if (hasChildren && !collapsed && !alwaysExpanded) {
      e.preventDefault();
      setExpanded((prev) => !prev);
    }
    if (link.onClick) link.onClick(e);
  };

  // Position the flyout using fixed positioning to escape overflow
  useEffect(() => {
    if (showFlyout && triggerRef.current && flyoutRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      flyoutRef.current.style.top = `${rect.top}px`;
      flyoutRef.current.style.left = `${rect.right + 4}px`;
    }
  }, [showFlyout]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const sizeClasses = nested
    ? 'gap-2.5 text-[13px] py-2 px-3'
    : 'gap-3 text-sm py-2.5 px-3';
  const collapsedClasses = collapsed && !nested ? 'justify-center px-0' : '';

  if (link.disabled) {
    return (
      <KoobTooltip
        tip={link.disabledTip ?? (collapsed ? link.label : undefined)}
      >
        <div
          className={cn(
            'koob-sidebar-link relative flex items-center rounded-lg font-medium border-none w-full text-left',
            sizeClasses,
            collapsedClasses,
            'text-foreground/30 select-none',
            link.onDisabledClick ? 'cursor-pointer' : 'cursor-not-allowed',
          )}
          onClick={link.onDisabledClick}
          data-testid={link.dataCy}
        >
          {link.icon && (
            <i
              className={cn(
                link.icon,
                'fa-fw text-base shrink-0',
                collapsed && !nested && 'text-lg',
              )}
            />
          )}
          {!collapsed && <span className="flex-1 truncate">{link.label}</span>}
        </div>
      </KoobTooltip>
    );
  }

  const linkElement = (
    <NavLink
      to={link.to}
      end={hasChildren ? undefined : link.end}
      className={cn(
        baseClasses,
        sizeClasses,
        collapsedClasses,
        inactiveClasses,
        hasChildren && 'koob-sidebar-link--parent',
        isExtraMatchActive && 'active',
      )}
      title={collapsed ? link.label : undefined}
      data-testid={link.dataCy}
      onClick={hasChildren && !collapsed ? handleParentClick : link.onClick}
    >
      {link.icon && (
        <i
          className={cn(
            link.icon,
            'fa-fw text-base shrink-0',
            collapsed && !nested && 'text-lg',
          )}
        />
      )}
      {!collapsed && <span className="flex-1 truncate">{link.label}</span>}
      {!collapsed && link.badge != null && link.badge > 0 && (
        <KoobSidebarBadge count={link.badge} />
      )}
      {!collapsed && hasChildren && !alwaysExpanded && (
        <i
          className={cn(
            'far fa-chevron-down fa-fw text-xs transition-transform duration-200',
            expanded && 'rotate-180',
          )}
        />
      )}
    </NavLink>
  );

  if (collapsed && hasChildren) {
    return (
      <div
        ref={triggerRef}
        className="relative"
        onMouseEnter={openFlyout}
        onMouseLeave={closeFlyoutWithDelay}
      >
        {linkElement}
        {showFlyout && (
          <KoobSidebarFlyout
            ref={flyoutRef}
            parentLabel={link.label}
            childLinks={link.children}
            onMouseEnter={openFlyout}
            onMouseLeave={closeFlyoutWithDelay}
          />
        )}
      </div>
    );
  }

  return (
    <div>
      {linkElement}

      {hasChildren && isExpanded && !collapsed && (
        <div className="mt-1 ml-4 space-y-0.5 border-l border-border pl-4">
          {/* A child can be an action rather than a route (opening the trip
              wizard over the page), so it takes the same to/onClick fork. */}
          {link.children.map((child, index) =>
            child.to || child.disabled ? (
              <NavLinkItem
                key={index}
                link={child}
                collapsed={collapsed}
                nested
              />
            ) : (
              <StaticContent
                key={index}
                link={child}
                collapsed={collapsed}
                nested
              />
            ),
          )}
        </div>
      )}
    </div>
  );
}

function StaticContent({ link, collapsed, nested }) {
  const sizeClasses = nested
    ? 'gap-2.5 text-[13px] py-2 px-3'
    : 'gap-3 text-sm py-2.5 px-3';
  const collapsedClasses = collapsed && !nested ? 'justify-center px-0' : '';

  const content = (
    <>
      {link.icon && (
        <i
          className={cn(
            link.icon,
            'fa-fw text-base shrink-0',
            collapsed && !nested && 'text-lg',
          )}
        />
      )}
      {!collapsed && <span className="flex-1 truncate">{link.label}</span>}
      {!collapsed && link.badge != null && link.badge > 0 && (
        <KoobSidebarBadge count={link.badge} />
      )}
    </>
  );

  if (link.href) {
    return (
      <a
        href={link.href}
        target={link.external ? '_blank' : undefined}
        rel={link.external ? 'noreferrer' : undefined}
        className={cn(
          baseClasses,
          sizeClasses,
          collapsedClasses,
          inactiveClasses,
        )}
        title={collapsed ? link.label : undefined}
        data-testid={link.dataCy}
      >
        {content}
      </a>
    );
  }

  if (link.onClick) {
    return (
      <button
        onClick={link.onClick}
        className={cn(
          baseClasses,
          sizeClasses,
          collapsedClasses,
          inactiveClasses,
          'bg-transparent',
        )}
        title={collapsed ? link.label : undefined}
        data-testid={link.dataCy}
      >
        {content}
      </button>
    );
  }

  return null;
}

export default function KoobSidebarLink({
  link,
  collapsed = false,
  nested = false,
}) {
  if (link.to) {
    return <NavLinkItem link={link} collapsed={collapsed} nested={nested} />;
  }

  return <StaticContent link={link} collapsed={collapsed} nested={nested} />;
}
